import type { JSX } from "react";
import { FC, useState, useCallback, useMemo } from "react";
import styled from "styled-components";
import { color as themeColor, colour } from "@styles";
import { HeadingSize, H2, H3 } from "../../atoms/Heading";
import { P1 } from "../../atoms/Paragraph";
import { TabSwitcherDesktop } from "./TabSwitcherDesktop";
import { TabSwitcherMobile } from "./TabSwitcherMobile";
import { Cloud } from "../../atoms/Cloud";
import { StandardSection } from "components/templates/StandardSection";
import { tablet, mobile } from "components/templates/Breakpoints";
import { variationColor } from "./utils";

export interface Tab {
  title: string;
  heading?: string | JSX.Element;
  content?: string | JSX.Element;
  image?: string | JSX.Element;
  alt?: string;
  url?: string;
  callToAction?: string;
  footnote?: string;
}

export type TabSwitcherVariation = "pink" | "purple";

export type HandleClick = (index: number) => void;

export const TabSwitcher: FC<
  React.PropsWithChildren<{
    id?: string;
    title?: string | JSX.Element;
    content?: string;
    tabs: Tab[];
    imageOnly?: boolean;
    withClouds?: boolean;
    pt?: string;
    pb?: string;
    hugTop?: boolean;
    variation?: TabSwitcherVariation;
  }>
> = ({ id, title, content, tabs, imageOnly, withClouds, pt, pb, hugTop, variation }) => {
  const tabTitles = useMemo(() => tabs.map(({ title }) => title), [tabs]);
  const [selectedTab, setSelectedTab] = useState(0);

  const handleClick = useCallback(
    (index: number) => {
      if (index <= 0) {
        setSelectedTab(0);
      } else if (index >= tabs.length - 1) {
        setSelectedTab(tabs.length - 1);
      } else {
        setSelectedTab(index);
      }
    },
    [setSelectedTab, tabs],
  );

  return (
    <Container id={id} aria-labelledby={`${id}-tabswitcher-title`} variation={variation}>
      {!hugTop && (
        <StandardSection maxWidth="53rem" pt={pt} pb="0">
          {typeof title === "string" ? (
            <H3 id={`${id}-tabswitcher-title`} size={HeadingSize.H1} align="center">
              {title}
            </H3>
          ) : (
            <div id={`${id}-tabswitcher-title`}>{title}</div>
          )}
          <P1 pl="3rem" pr="3rem">
            {content}
          </P1>
        </StandardSection>
      )}
      <Desktop>
        <TabSwitcherDesktop
          variation={variation}
          id={id}
          tabs={tabs}
          tabTitles={tabTitles}
          handleClick={handleClick}
          selectedTab={selectedTab}
          imageOnly={imageOnly}
          clouds={withClouds ? clouds : null}
          pb={pb}
          hugTop={hugTop}
        />
      </Desktop>
      <Mobile>
        <TabSwitcherMobile
          id={id}
          tabs={tabs}
          tabTitles={tabTitles}
          handleClick={handleClick}
          selectedTab={selectedTab}
          imageOnly={imageOnly}
          clouds={withClouds ? clouds : null}
          pb={pb}
        />
      </Mobile>
    </Container>
  );
};

const clouds = (
  <>
    <Cloud id="tabswitcher-cloud1" color="purple" scale={1.83} top="6rem" right="85%" animated />
    <Cloud id="tabswitcher-cloud2" color="purple" scale={1.41} top="9rem" right="5%" animated />
    <Cloud
      id="tabswitcher-cloud3"
      color="purple"
      scale={1.37}
      reversed
      top="29rem"
      right="70%"
      animated
    />
  </>
);

const Container = styled.section<{ variation: TabSwitcherVariation }>`
  background: ${({ variation }) =>
    variation ? variationColor(variation).background : themeColor.S310};
  text-align: center;
  min-height: 80rem;

  ${tablet} {
    min-height: 90rem;
    padding: 0;
  }

  ${mobile} {
    min-height: 80rem;
    padding-top: 2rem;
  }
`;

const Desktop = styled.div`
  display: none;

  @media only screen and (min-width: 70rem) {
    display: block;
  }
`;

const Mobile = styled.div`
  display: none;

  @media only screen and (max-width: 70rem) {
    display: block;
  }
`;
